import { athenaClient } from './athena-client';
import {
  Appointment,
  AppointmentType,
  HPI,
  HealthHistoryForm,
  Allergy,
  Medication,
  Pharmacy,
  CareTeamMember,
  PatientCheckIn,
  AthenaWebhookEvent,
} from './athena-types';

export class AthenaServices {
  // Use Case #1: Triggering Automated Patient Outreach
  // Action: Patient is checked-in in athena (Real time webhook)
  // Cadence: Real time webhook
  // API Endpoint: POST /v1/{practiceid}/appointments/{appointmentid}/checkin
  
  static async handlePatientCheckIn(appointmentId: string): Promise<PatientCheckIn> {
    try {
      // 1. Trigger check-in via API
      const checkInResult = await athenaClient.checkInPatient(appointmentId);
      
      // 2. Retrieve appointment details
      const appointment = await athenaClient.getAppointment(appointmentId);
      
      // 3. Get appointment type for context
      const appointmentType = await athenaClient.getAppointmentType(appointment.appointmentTypeId);
      
      // 4. Trigger automated patient outreach based on appointment type
      await this.triggerPatientOutreach(appointment, appointmentType);
      
      return checkInResult;
    } catch (error) {
      console.error('Error handling patient check-in:', error);
      throw error;
    }
  }

  // 설문 완료 시 Athena 통합 실행
  static async handleSurveyCompletion(params: {
    visitId: string;
    appointmentId?: string;
    encounterId?: string;
    hpiData?: any;
    healthHistoryData?: {
      formId: string;
      formData: any;
    };
  }): Promise<any> {
    try {
      // TODO: [HIGH] - Insecure token retrieval from localStorage
      // ISSUE: JWT tokens stored in localStorage are vulnerable to XSS attacks and persist across browser sessions
      // RECOMMENDATION: Use httpOnly cookies or sessionStorage with proper encryption and short TTL
      // 인증 토큰 가져오기
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');

      const response = await fetch(`${athenaClient.config.baseUrl}/api/athena/survey-completion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Survey completion Athena integration result:', result);
      return result;
    } catch (error) {
      console.error('Error handling survey completion:', error);
      throw error;
    }
  }

  // Patient Check-in만 실행
  static async checkInPatient(appointmentId: string): Promise<any> {
    try {
      // TODO: [HIGH] - Repeated insecure token access pattern
      // ISSUE: Multiple instances of localStorage token access create consistent security vulnerabilities
      // RECOMMENDATION: Create centralized secure token management service
      // 인증 토큰 가져오기
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');

      const response = await fetch(`${athenaClient.config.baseUrl}/api/athena/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ appointmentId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Patient check-in result:', result);
      return result;
    } catch (error) {
      console.error('Error checking in patient:', error);
      throw error;
    }
  }

  // HPI 업데이트
  static async updateHPI(encounterId: string, hpiData: any): Promise<any> {
    try {
      // TODO: [HIGH] - Consistent localStorage token vulnerability pattern
      // ISSUE: Same insecure token retrieval pattern repeated across all Athena service methods
      // RECOMMENDATION: Refactor to use secure token management with proper validation
      // 인증 토큰 가져오기
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');

      const response = await fetch(`${athenaClient.config.baseUrl}/api/athena/hpi`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ encounterId, hpiData }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('HPI update result:', result);
      return result;
    } catch (error) {
      console.error('Error updating HPI:', error);
      throw error;
    }
  }

  // 설문 데이터에서 HPI 생성
  static async generateHPIFromSurvey(systemValueMap: any): Promise<any> {
    try {
      // TODO: [HIGH] - Final instance of localStorage token vulnerability
      // ISSUE: All Athena service methods use the same insecure token retrieval pattern
      // RECOMMENDATION: Implement comprehensive security refactor with centralized token management
      // 인증 토큰 가져오기
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');

      const response = await fetch(`${athenaClient.config.baseUrl}/api/athena/generate-hpi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ systemValueMap }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('HPI generation result:', result);
      return result;
    } catch (error) {
      console.error('Error generating HPI from survey:', error);
      throw error;
    }
  }

  // Webhook event handler for appointment check-in
  static async handleAppointmentCheckInWebhook(webhookEvent: AthenaWebhookEvent): Promise<void> {
    try {
      if (webhookEvent.eventType === 'appointment.checkin') {
        const appointmentId = webhookEvent.resourceId;
        
        // 1. Retrieve appointment details
        const appointment = await athenaClient.getAppointment(appointmentId);
        
        // 2. Get appointment type for context
        const appointmentType = await athenaClient.getAppointmentType(appointment.appointmentTypeId);
        
        // 3. Extract basic patient information (Name, Sex, Date of Birth, mobile number)
        const patientInfo = {
          name: appointment.patient.name,
          sex: appointment.patient.sex,
          dateOfBirth: appointment.patient.dateOfBirth,
          mobileNumber: appointment.patient.mobileNumber,
          email: appointment.patient.email,
        };
        
        // 4. Trigger automated patient outreach based on appointment type
        await this.triggerPatientOutreach(appointment, appointmentType);
        
        console.log(`Processed check-in webhook for appointment ${appointmentId}`);
      }
    } catch (error) {
      console.error('Error handling appointment check-in webhook:', error);
      throw error;
    }
  }

  // Method to get appointmentId from patientId and date
  static async getAppointmentIdByPatientAndDate(patientId: string, date: string): Promise<string | null> {
    try {
      const appointments = await athenaClient.getAppointmentsByPatient(patientId, date);
      const todayAppointment = appointments.find((appt: any) => 
        appt.scheduledDate.startsWith(date) && appt.status === 'scheduled'
      );
      return todayAppointment?.appointmentId || null;
    } catch (error) {
      console.error('Error getting appointment ID by patient and date:', error);
      return null;
    }
  }

  // Method to get appointmentId from patientId and appointment type
  static async getAppointmentIdByPatientAndType(patientId: string, appointmentTypeId: string): Promise<string | null> {
    try {
      const appointments = await athenaClient.getAppointmentsByPatient(patientId);
      const matchingAppointment = appointments.find((appt: any) => 
        appt.appointmentTypeId === appointmentTypeId && appt.status === 'scheduled'
      );
      return matchingAppointment?.appointmentId || null;
    } catch (error) {
      console.error('Error getting appointment ID by patient and type:', error);
      return null;
    }
  }

  // Method to get today's appointments for a patient
  static async getTodaysAppointments(patientId: string): Promise<Appointment[]> {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const appointments = await athenaClient.getAppointmentsByPatient(patientId, today);
      return appointments.filter((appt: any) => appt.status === 'scheduled');
    } catch (error) {
      console.error('Error getting today\'s appointments:', error);
      return [];
    }
  }

  // Method to get next appointment for a patient
  static async getNextAppointment(patientId: string): Promise<Appointment | null> {
    try {
      const appointments = await athenaClient.getAppointmentsByPatient(patientId);
      const futureAppointments = appointments.filter((appt: any) => 
        new Date(appt.scheduledDate) > new Date() && appt.status === 'scheduled'
      );
      
      if (futureAppointments.length === 0) return null;
      
      // Sort by date and return the earliest
      futureAppointments.sort((a: any, b: any) => 
        new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
      );
      
      return futureAppointments[0];
    } catch (error) {
      console.error('Error getting next appointment:', error);
      return null;
    }
  }

  // Method to get appointment by encounter ID (for HPI use case)
  static async getAppointmentByEncounter(encounterId: string): Promise<Appointment | null> {
    try {
      const appointment = await athenaClient.getAppointmentByEncounter(encounterId);
      return appointment;
    } catch (error) {
      console.error('Error getting appointment by encounter:', error);
      return null;
    }
  }

  private static async triggerPatientOutreach(appointment: Appointment, appointmentType: AppointmentType) {
    // Implement patient outreach logic based on appointment type
    console.log(`Triggering outreach for ${appointmentType.name} appointment`);
    
    // This could trigger:
    // - SMS notifications
    // - Email reminders
    // - Survey invitations
    // - Pre-visit questionnaires
  }

  // Use Case #2: Gathering Reason for Visit & History of Present Illness (HPI)
  // Action: Check if anything has been entered in the current HPI note section
  // Cadence: Upon Patient Chart Access
  // API Endpoint: GET /v1/{practiceid}/chart/encounter/{encounterid}/hpi
  
  static async getCurrentHPI(encounterId: string): Promise<HPI | null> {
    try {
      const hpi = await athenaClient.getHPI(encounterId);
      return hpi;
    } catch (error) {
      console.error('Error fetching HPI:', error);
      return null;
    }
  }

  // Action: CyrenCare writes patient generated HPI into the HPI section
  // Cadence: Upon Patient Submission
  // API Endpoint: PUT /v1/{practiceid}/chart/encounter/{encounterid}/hpi
  
  static async updateHPIWithPatientData(encounterId: string, patientGeneratedHPI: string): Promise<HPI> {
    try {
      // Combine existing HPI with patient-generated content
      const existingHPI = await this.getCurrentHPI(encounterId);
      const combinedContent = existingHPI 
        ? `${existingHPI.content}\n\nPatient Generated HPI:\n${patientGeneratedHPI}`
        : patientGeneratedHPI;
      
      const hpiData = {
        content: combinedContent,
        status: 'pending',
        lastModified: new Date().toISOString(),
      };
      
      const updatedHPI = await athenaClient.updateHPI(encounterId, hpiData);
      return updatedHPI;
    } catch (error) {
      console.error('Error updating HPI:', error);
      throw error;
    }
  }

  // Use Case #3: Reviewing and Updating Health History Information
  // Action: For existing patients, CyrenCare reads existing allergy information from Athena via API
  // Cadence: Upon Patient Chart Access
  // API Endpoint: GET /v1/{practiceid}/appointments/{appointmentid}/healthhistoryforms
  
  static async getHealthHistoryForms(appointmentId: string): Promise<HealthHistoryForm[]> {
    try {
      const forms = await athenaClient.getHealthHistoryForms(appointmentId);
      return forms;
    } catch (error) {
      console.error('Error fetching health history forms:', error);
      return [];
    }
  }

  // Action: CyrenCare updates and writes the confirmed or edited allergy information back into Athena
  // Cadence: On Information Confirmation/Modification
  // API Endpoint: PUT /v1/{practiceid}/appointments/{appointmentid}/healthhistoryforms/{formid}
  
  static async updateHealthHistoryForm(
    appointmentId: string,
    formId: string,
    formData: any,
    status: 'pending' | 'confirmed' | 'modified' = 'pending'
  ): Promise<HealthHistoryForm> {
    try {
      const updatedForm = await athenaClient.updateHealthHistoryForm(appointmentId, formId, {
        ...formData,
        status,
        lastModified: new Date().toISOString(),
      });
      return updatedForm;
    } catch (error) {
      console.error('Error updating health history form:', error);
      throw error;
    }
  }

  // Allergy Management
  static async getPatientAllergies(appointmentId: string): Promise<Allergy[]> {
    try {
      const forms = await this.getHealthHistoryForms(appointmentId);
      const allergyForm = forms.find(form => form.formType === 'allergies');
      return allergyForm?.content?.allergies || [];
    } catch (error) {
      console.error('Error fetching patient allergies:', error);
      return [];
    }
  }

  static async updatePatientAllergies(
    appointmentId: string,
    formId: string,
    allergies: Allergy[]
  ): Promise<HealthHistoryForm> {
    return this.updateHealthHistoryForm(appointmentId, formId, {
      allergies: allergies.map(allergy => ({
        ...allergy,
        status: 'pending' as const,
      })),
    });
  }

  // Medication Management
  static async getPatientMedications(appointmentId: string): Promise<Medication[]> {
    try {
      const forms = await this.getHealthHistoryForms(appointmentId);
      const medicationForm = forms.find(form => form.formType === 'medications');
      return medicationForm?.content?.medications || [];
    } catch (error) {
      console.error('Error fetching patient medications:', error);
      return [];
    }
  }

  static async updatePatientMedications(
    appointmentId: string,
    formId: string,
    medications: Medication[]
  ): Promise<HealthHistoryForm> {
    return this.updateHealthHistoryForm(appointmentId, formId, {
      medications: medications.map(medication => ({
        ...medication,
        status: 'pending' as const,
      })),
    });
  }

  // Pharmacy Management
  // Action: For existing patients, CyrenCare reads existing pharmacy information from Athena via API
  // Cadence: On Patient Information Modification
  // API Endpoints: 
  // - GET /v1/{practiceid}/chart/{patientid}/pharmacies/preferred
  // - GET /v1/{practiceid}/chart/{patientid}/pharmacies/default
  
  static async getPatientPharmacies(patientId: string): Promise<Pharmacy[]> {
    try {
      const [preferredPharmacies, defaultPharmacy] = await Promise.all([
        athenaClient.getPreferredPharmacies(patientId),
        athenaClient.getDefaultPharmacy(patientId),
      ]);

      return [
        ...preferredPharmacies,
        ...(defaultPharmacy ? [defaultPharmacy] : []),
      ];
    } catch (error) {
      console.error('Error fetching patient pharmacies:', error);
      return [];
    }
  }

  // Action: CyrenCare updates and writes the confirmed or edited pharmacy information back into Athena
  // Cadence: On Patient Information Modification
  // API Endpoints:
  // - PUT /v1/{practiceid}/chart/{patientid}/pharmacies/preferred
  // - PUT /v1/{practiceid}/chart/{patientid}/pharmacies/default
  
  static async updatePatientPharmacy(
    patientId: string,
    pharmacy: Pharmacy,
    isDefault = false
  ): Promise<Pharmacy> {
    try {
      if (isDefault) {
        return await athenaClient.updateDefaultPharmacy(patientId, pharmacy);
      }
      
      // For preferred pharmacies, we can either add new or update existing
      const existingPharmacies = await athenaClient.getPreferredPharmacies(patientId);
      const existingPharmacy = existingPharmacies.find((p: any) => p.id === pharmacy.id);
      
      if (existingPharmacy) {
        return await athenaClient.updatePreferredPharmacy(patientId, pharmacy);
      }
      
      return await athenaClient.addPreferredPharmacy(patientId, pharmacy);
    } catch (error) {
      console.error('Error updating patient pharmacy:', error);
      throw error;
    }
  }

  // Care Team Management (PCP)
  // Action: For existing patients, CyrenCare reads existing PCP information from Athena via API
  // Cadence: On Information Confirmation/Modification
  // API Endpoint: GET /v1/{practiceid}/chart/{patientid}/careteam
  
  static async getPatientCareTeam(patientId: string): Promise<CareTeamMember[]> {
    try {
      const careTeam = await athenaClient.getCareTeam(patientId);
      return careTeam;
    } catch (error) {
      console.error('Error fetching patient care team:', error);
      return [];
    }
  }

  // Action: CyrenCare updates and writes the confirmed or edited PCP information back into Athena
  // Cadence: On Information Confirmation/Modification
  // API Endpoint: PUT /v1/{practiceid}/chart/{patientid}/careteam
  
  static async updatePatientCareTeam(
    patientId: string,
    careTeam: CareTeamMember[]
  ): Promise<CareTeamMember[]> {
    try {
      const updatedCareTeam = await athenaClient.updateCareTeam(patientId, careTeam);
      return updatedCareTeam;
    } catch (error) {
      console.error('Error updating patient care team:', error);
      throw error;
    }
  }

  // Pharmacy Organization Lookup
  // API Endpoint: GET /fhir/r4/Organization?type=pharmacy
  
  static async searchPharmacyOrganizations(query: string): Promise<any[]> {
    try {
      const organizations = await athenaClient.getPharmacyOrganizations();
      return organizations.filter((org: any) => 
        org.name.toLowerCase().includes(query.toLowerCase())
      );
    } catch (error) {
      console.error('Error searching pharmacy organizations:', error);
      return [];
    }
  }

  // Book Appointment Slots within athenaOne 플로우

  // 1. Get a list of departments
  static async getDepartments(): Promise<any> {
    try {
      const departments = await athenaClient.getDepartments();
      return departments;
    } catch (error) {
      console.error('Error fetching departments:', error);
      return [];
    }
  }

  // 2. Get a list of providers
  static async getProviders(): Promise<any> {
    try {
      const providers = await athenaClient.getProviders();
      return providers;
    } catch (error) {
      console.error('Error fetching providers:', error);
      return [];
    }
  }

  // 3. Get patient appointment reasons
  static async getPatientAppointmentReasons(reasonType?: string): Promise<any> {
    try {
      const reasons = await athenaClient.getPatientAppointmentReasons(reasonType);
      return reasons;
    } catch (error) {
      console.error('Error fetching patient appointment reasons:', error);
      return [];
    }
  }

  // 4. Get open appointments
  static async getOpenAppointments(params: {
    reasonid?: string;
    departmentid?: string;
    providerid?: string;
    startdate?: string;
    enddate?: string;
  }): Promise<any> {
    try {
      const appointments = await athenaClient.getOpenAppointments(params);
      return appointments;
    } catch (error) {
      console.error('Error fetching open appointments:', error);
      return [];
    }
  }

  // 5a. Create a new patient
  static async createPatient(patientData: {
    firstname: string;
    lastname: string;
    dob: string;
    departmentid: string;
    homephone?: string;
    mobilephone?: string;
    workphone?: string;
  }): Promise<any> {
    try {
      console.log('AthenaServices.createPatient called with:', patientData);
      const patient = await athenaClient.createPatient(patientData);
      console.log('AthenaServices.createPatient result:', patient);
      return patient;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  }

  // 5b. Find existing patient
  static async findPatient(searchParams: {
    firstname: string;
    lastname: string;
    dob: string;
    departmentid: string;
    homephone?: string;
    mobilephone?: string;
    workphone?: string;
  }): Promise<any> {
    try {
      const patients = await athenaClient.findPatient(searchParams);
      return patients;
    } catch (error) {
      console.error('Error finding patient:', error);
      return [];
    }
  }

  // 6. Book a patient appointment
  static async bookPatientAppointment(appointmentId: string, bookingData: {
    patientid: string;
    reasonid: string;
    appointmentid: string;
  }): Promise<any> {
    try {
      const result = await athenaClient.bookPatientAppointment(appointmentId, bookingData);
      return result;
    } catch (error) {
      console.error('Error booking patient appointment:', error);
      throw error;
    }
  }

  // Complete appointment booking workflow
  static async completeAppointmentBooking(workflowData: {
    // Step 1-3: Get available options
    selectedDepartmentId?: string;
    selectedProviderId?: string;
    selectedReasonId?: string;
    
    // Step 4: Get open appointments
    startDate?: string;
    endDate?: string;
    
    // Step 5: Patient information
    patientInfo: {
      firstname: string;
      lastname: string;
      dob: string;
      departmentid: string;
      homephone?: string;
      mobilephone?: string;
      workphone?: string;
    };
    
    // Step 6: Final booking
    selectedAppointmentId?: string;
  }): Promise<any> {
    try {
      const {
        selectedDepartmentId,
        selectedProviderId,
        selectedReasonId,
        startDate,
        endDate,
        patientInfo,
        selectedAppointmentId,
      } = workflowData;

      // Step 1-3: Get available options (if not provided)
      let departments;
      let providers;
      let reasons;
      
      if (!selectedDepartmentId) {
        departments = await this.getDepartments();
      }
      
      if (!selectedProviderId) {
        providers = await this.getProviders();
      }
      
      if (!selectedReasonId) {
        reasons = await this.getPatientAppointmentReasons();
      }

      // Step 4: Get open appointments
      const openAppointments = await this.getOpenAppointments({
        reasonid: selectedReasonId,
        departmentid: selectedDepartmentId,
        providerid: selectedProviderId,
        startdate: startDate,
        enddate: endDate,
      });

      // Step 5: Handle patient (create new or find existing)
      let patientId;
      try {
        // Try to find existing patient
        const existingPatients = await this.findPatient(patientInfo);
        if (existingPatients.length > 0) {
          patientId = existingPatients[0].patientid;
        } else {
          // Create new patient
          const newPatient = await this.createPatient(patientInfo);
          patientId = newPatient.patientid;
        }
      } catch (error) {
        console.error('Error handling patient:', error);
        throw error;
      }

      // Step 6: Book appointment
      if (selectedAppointmentId && patientId && selectedReasonId) {
        const bookingResult = await this.bookPatientAppointment(selectedAppointmentId, {
          patientid: patientId,
          reasonid: selectedReasonId,
          appointmentid: selectedAppointmentId,
        });

        return {
          success: true,
          bookingResult,
          patientId,
          availableOptions: {
            departments,
            providers,
            reasons,
          },
          openAppointments,
        };
      }

      return {
        success: false,
        message: 'Missing required data for booking',
        availableOptions: {
          departments,
          providers,
          reasons,
        },
        openAppointments,
        patientId,
      };
    } catch (error) {
      console.error('Error in complete appointment booking workflow:', error);
      throw error;
    }
  }

  static async putSectionNote(patientId: string, departmentId: string, note: string) {
    try {
      const result = await athenaClient.putSectionNote(patientId, departmentId, note);
      return result;
    } catch (error) {
      console.error('Error putting section note:', error);
      throw error;
    }
  }

  static async getEncounter(patientId: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.getEncounter(patientId, departmentId);
      return result;
    } catch (error) {
      console.error('Error getting encounter:', error);
      throw error;
    }
  }

  static async getEncounterById(encounterId: string): Promise<any> {
    try {
      const result = await athenaClient.getEncounterById(encounterId);
      return result;
    } catch (error) {
      console.error('Error getting encounter by id:', error);
      throw error;
    }
  }

  static async updateHpi(encounterId: string, hpiData: string, additionalSentence: boolean): Promise<any> {
    try {
      const data = {
        "sentences": [
          {
            "findings": [
              {
                "freetext": hpiData + additionalSentence,
                "findingid": "string",
                "findingnote": "string"
              }
            ],
            "sentenceid": "string",
            "sentencenote": "string"
          }
        ],
        "templateid": 0,
        "paragraphid": "string"
      }

      const result = await athenaClient.updateHpi(encounterId, data);
      return result;
    } catch (error) {
      console.error('Error updating HPI:', error);
      throw error;
    }
  }

  static async putHPI(encounterId: string, hpiData: string): Promise<any> {
    try {
      const data = {"sectionnote": hpiData, "replacesectionnote": true, "hpitoros": false}
      const result = await athenaClient.putHPI(encounterId, data);
      return result;
    } catch (error) {
      console.error('Error updating HPI:', error);
      throw error;
    }
  }

  static async getPatientDetails(patientId: string): Promise<any> {
    try {
      const result = await athenaClient.getPatientDetails(patientId);
      return result;
    } catch (error) {
      console.error('Error getting patient details:', error);
      throw error;
    }
  }

  static async getHealthHistoryForm(appointmentId: string): Promise<any> {
    try {
      const result = await athenaClient.getHealthHistoryForm(appointmentId);
      return result;
    } catch (error) {
      console.error('Error getting health history form:', error);
      throw error;
    }
  }

  static async checkInfo(aid: string, lastName: string, birthDate: string): Promise<any> {
    try {
      const result = await athenaClient.checkInfo(aid, lastName, birthDate);
      return result;
    } catch (error) {
      console.error('Error checking patient info:', error);
      throw error;
    }
  }

  static async getCCDA(patientId: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.getCCDA(patientId, departmentId);
      return result;
    } catch (error) {
      console.error('Error getting CCDA:', error);
      throw error;
    }
  }

  static async updateCCDA(patientId: string, ccda: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.updateCCDA(patientId, ccda, departmentId);
      return result;
    } catch (error) {
      console.error('Error updating CCDA:', error);
      throw error;
    }
  }

  static async getMedication(medication: string): Promise<any> {
    try {
      const result = await athenaClient.getMedication(medication);
      return result;
    } catch (error) {
      console.error('Error getting medication:', error);
      throw error;
    }
  }

  static async getAllergy(allergy: string): Promise<any> {
    try {
      const result = await athenaClient.getAllergy(allergy);
      return result;
    } catch (error) {
      console.error('Error getting allergy:', error);
      throw error;
    }
  }
  
  static async getReaction(): Promise<any> {
    try {
      const result = await athenaClient.getReaction();
      return result;
    } catch (error) {
      console.error('Error searching reaction:', error);
      throw error;
    }
  }

  static async putAllergy(patientId: string, departmentId: string, allergies: any[], reactions: any[]): Promise<any> {
    try {
      const result = await athenaClient.putAllergy(patientId, departmentId, allergies, reactions);
      return result;
    } catch (error) {
      console.error('Error putting allergy:', error);
      throw error;
    }
  }

  static async postMedication(patientId: string, departmentId: string, medicationId: string): Promise<any> {
    try {
      const result = await athenaClient.postMedication(patientId, departmentId, medicationId);
      return result;
    } catch (error) {
      console.error('Error posting medication:', error);
      throw error;
    }
  } 

  static async searchProvider(zip: string, name: string, orderType: string, signal?: AbortSignal): Promise<any> {
    try {
      if (orderType === 'CONSULT') {
        orderType = 'CONSULT';
      } else if (orderType === 'PRESCRIPTION') {
        orderType = 'PRESCRIPTION';
      } else {
        orderType = 'CONSULT';
      }
      const result = await athenaClient.searchProvider(zip, name, orderType, signal);
      return result;
    } catch (error: any) {
      // AbortError는 상위로 전달하지 않음
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        throw error;
      }
      console.error('Error searching provider:', error);
      throw error;
    }
  }

  static async putPharmacy(patientId: string, departmentId: string, pharmacyId: string): Promise<any> {
    try {
      const result = await athenaClient.putPharmacy(patientId, departmentId, pharmacyId);
      return result;
    } catch (error) {
      console.error('Error putting pharmacy:', error);
      throw error;
    }
  }

  static async putProvider(patientId: string, departmentId: string, providerId: string): Promise<any> {
    try {
      const result = await athenaClient.putProvider(patientId, departmentId, providerId);
      return result;
    } catch (error) {
      console.error('Error putting provider:', error);
      throw error;
    }
  }

  static async findExistProvider(patientId: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.findExistProvider(patientId, departmentId);
      return result;
    } catch (error) {
      console.error('Error finding exist provider:', error);
      throw error;
    }
  }

  static async findExistPharmacy(patientId: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.findExistPharmacy(patientId, departmentId);
      return result;
    } catch (error) {
      console.error('Error finding exist pharmacy:', error);
      throw error;
    }
  }

  static async findExistMedication(patientId: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.findExistMedication(patientId, departmentId);
      return result;
    } catch (error) {
      console.error('Error finding exist medication:', error);
      throw error;
    }
  }

  static async findExistAllergy(patientId: string, departmentId: string): Promise<any> {
    try {
      const result = await athenaClient.findExistAllergy(patientId, departmentId);
      return result;
    } catch (error) {
      console.error('Error finding exist allergy:', error);
      throw error;
    }
  }

  static async checkEncounterStatus(encounterId: string): Promise<any> {
    try {
      const result = await athenaClient.checkEncounterStatus(encounterId);
      return result;
    } catch (error) {
      console.error('Error checking encounter status:', error);
      throw error;
    }
  }

  static async handleHpiUpdate(encounterId: string, prompt: string, additionalText: string, visitId?: string): Promise<any> {
    try {
      const result = await athenaClient.handleHpiUpdate(encounterId, prompt, additionalText, visitId);
      return result;
    } catch (error) {
      console.error('Error handling HPI update:', error);
      throw error;
    }
  }

  static async getCCDAExport(patientId: string, departmentId: string, encounterId: string): Promise<any> {
    try {
      const result = await athenaClient.getCCDAExport(patientId, departmentId, encounterId);
      return result;
    } catch (error) {
      console.error('Error getting CCDA export:', error);
      throw error;
    }
  }

  static async getHPIForUpdate(encounterId: string): Promise<any> {
    try {
      const result = await athenaClient.getHPIForUpdate(encounterId);
      return result;
    } catch (error) {
      console.error('Error getting HPI for update:', error);
      throw error;
    }
  }

  static async createFhirSubscription(deploymentId: string): Promise<any> {
    try {
      const result = await athenaClient.createFhirSubscription(deploymentId);
      return result;
    } catch (error) {
      console.error('Error creating FHIR Subscription:', error);
      throw error;
    }
  }

  static async deleteFhirSubscription(deploymentId: string): Promise<any> {
    try {
      const result = await athenaClient.deleteFhirSubscription(deploymentId);
      return result;
    } catch (error) {
      console.error('Error deleting FHIR Subscription:', error);
      throw error;
    }
  }

  static async checkSurveyStatusByAid(aid: string): Promise<any> {
    try {
      const result = await athenaClient.checkSurveyStatusByAid(aid);
      return result;
    } catch (error) {
      console.error('Error checking survey status by aid:', error);
      throw error;
    }
  }
} 